import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Avatar,
    Stack,
    TextField,
    InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { getAllUsers, updateUserRole, getUserRole } from '../../firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_COLORS = {
    user: 'default',
    premium_user: 'primary',
    admin: 'warning',
    superadmin: 'error'
};

const ROLE_LABELS = {
    user: 'Użytkownik',
    premium_user: 'Użytkownik Premium',
    admin: 'Administrator',
    superadmin: 'Super Administrator'
};

function UserManagement() {
    const { currentUser, userData } = useAuth();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newRole, setNewRole] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [updating, setUpdating] = useState(false);

    // Sprawdź czy użytkownik ma uprawnienia superadmin
    const isSuperAdmin = userData?.role === 'superadmin';

    useEffect(() => {
        loadUsers();
    }, []);

    useEffect(() => {
        filterUsers();
    }, [users, searchTerm]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const usersData = await getAllUsers();
            setUsers(usersData);
        } catch (error) {
            console.error('Błąd podczas ładowania użytkowników:', error);
            setError('Nie udało się załadować listy użytkowników');
        } finally {
            setLoading(false);
        }
    };

    const filterUsers = () => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
            return;
        }

        const filtered = users.filter(user =>
            user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredUsers(filtered);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setNewRole(user.role || 'user');
        setEditDialogOpen(true);
    };

    const handleUpdateRole = async () => {
        if (!selectedUser || !newRole) return;

        try {
            setUpdating(true);
            await updateUserRole(selectedUser.id, newRole);

            // Aktualizuj lokalną listę użytkowników
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.id === selectedUser.id
                        ? { ...user, role: newRole }
                        : user
                )
            );

            setEditDialogOpen(false);
            setSelectedUser(null);
            setNewRole('');
        } catch (error) {
            console.error('Błąd podczas aktualizacji roli:', error);
            setError('Nie udało się zaktualizować roli użytkownika');
        } finally {
            setUpdating(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isSuperAdmin) {
        return (
            <Alert severity="error">
                Nie masz uprawnień do zarządzania użytkownikami. Wymagana rola: Super Administrator
            </Alert>
        );
    }

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Zarządzanie użytkownikami
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Zarządzaj rolami użytkowników w systemie. Tylko Super Administratorzy mogą zmieniać role.
                </Typography>

                <TextField
                    fullWidth
                    placeholder="Szukaj użytkowników po nazwie lub emailu..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Użytkownik</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Rola</TableCell>
                            <TableCell>Data utworzenia</TableCell>
                            <TableCell align="center">Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar
                                            src={user.photo_url}
                                            sx={{ width: 32, height: 32 }}
                                        >
                                            {!user.photo_url && getInitials(user.display_name)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {user.display_name || 'Brak nazwy'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {user.email}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={ROLE_LABELS[user.role] || 'Użytkownik'}
                                        color={ROLE_COLORS[user.role] || 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('pl-PL') : 'Nieznana'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Edytuj rolę">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditUser(user)}
                                            disabled={user.id === currentUser?.uid} // Nie można edytować własnej roli
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredUsers.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'Nie znaleziono użytkowników pasujących do wyszukiwania' : 'Brak użytkowników'}
                    </Typography>
                </Paper>
            )}

            {/* Dialog edycji roli */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edytuj rolę użytkownika
                </DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{ pt: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                                <Avatar
                                    src={selectedUser.photo_url}
                                    sx={{ width: 48, height: 48 }}
                                >
                                    {!selectedUser.photo_url && getInitials(selectedUser.display_name)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6">
                                        {selectedUser.display_name || 'Brak nazwy'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedUser.email}
                                    </Typography>
                                </Box>
                            </Stack>

                            <FormControl fullWidth>
                                <InputLabel>Rola</InputLabel>
                                <Select
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    label="Rola"
                                >
                                    <MenuItem value="user">Użytkownik</MenuItem>
                                    <MenuItem value="premium_user">Użytkownik Premium</MenuItem>
                                    <MenuItem value="admin">Administrator</MenuItem>
                                    <MenuItem value="superadmin">Super Administrator</MenuItem>
                                </Select>
                            </FormControl>

                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <strong>Uwaga:</strong> Zmiana roli użytkownika może wpłynąć na jego uprawnienia w systemie.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleUpdateRole}
                        variant="contained"
                        disabled={updating || !newRole}
                    >
                        {updating ? <CircularProgress size={20} /> : 'Zapisz'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default UserManagement;
